import type { Node } from "@xyflow/react";
import type { WSection } from "../types";

// ─────────────────────────────────────────────────────────────────────────────
// BAND 1 — what is left of it.
//
// This file used to be 667 lines holding ~50 units of PROSE crammed into board
// cards: the strategy sliders, the numbers, the plan, the content-strategy map,
// the five pains, four full page specs, the six-card competitor analysis, seven
// verbatim competitor matrices, and the whole SEO/GEO playbook.
//
// All of it is now KNOWLEDGE-BASE DOCUMENTS, rendered on the board through
// <DocumentFrame> (Erez-locked 2026-07-25: "if you READ it, it is a document;
// if you SCAN it, it stays a box"). Migrated 2026-07-25 — every unit was mapped
// to a destination and verified rendering BEFORE this deletion:
//
//   strategy/the-strategy ............ positioning · ICP · goals · competition · roadmap
//   strategy/the-numbers ............. intake · revenue · six-month forecast
//   strategy/lead-engine ............. ranked lead sources · build order
//   strategy/content-strategy ........ category/wedge/why-now · pillars · jobs · the close
//   competitors/the-analysis ......... the synthesis, + 7 verbatim cluster matrices
//   seo-geo .......................... the full SEO/GEO playbook + both matrices
//   site-and-pages ................... site map · page formula · 5 pains · 4 page specs
//                                      · the LOCKED homepage copy
//
// TWO things stayed board-native, and they are all this file still emits:
//   proc-strip — the marketing loop. LIVE STATE: you scan it, you don't read it.
//   wf-v1      — the homepage wireframe. An IMAGE; it now sits in Row 2 with the
//                other site frames. Its copy is ALSO in site-and-pages, so the
//                locked words are not trapped in a .tsx file.
// ─────────────────────────────────────────────────────────────────────────────

export function band1Strategy(): Node[] {
  const out: Node[] = [];
  const STRAT_X = 0;
  const WF_X = 7840;

  // ── the marketing loop — live state, board-native ──────────────────────────
  out.push({
    id: "proc-strip", type: "processStrip", position: { x: STRAT_X, y: 40 },
    data: { steps: ["Intake", "Assess", "Plan", "Produce", "Review"], current: 2, label: "Where we are · the marketing loop" },
    draggable: false, selectable: false,
  });

  // ── HOMEPAGE WIREFRAME — copy LOCKED (Erez, 2026-07-09) ────────────────────
  // The body IS the hero sentence expanded: learn your business · connect your
  // systems · develop the mind — one section each. The three pillars are named
  // and locked; the design pass is what comes next.
  const LOCK_HERO: WSection = {
    n: "01", name: "Hero", layout: "hero", eyebrow: "Your AI partner",
    goal: "Locked. The promise + the three-step arc the whole page expands.",
    headline: "Grow your business with the power of AI",
    sub: "We learn your business, connect your systems, and develop the mind that drives your business growth",
    cta: "Book a meeting",
  };
  const LOCK_LOGOS: WSection = {
    n: "02", name: "Trust row", layout: "logos",
    goal: "Locked. Borrow authority from the frontier AI we build on.",
    headline: "Built on the world's leading AI",
    logos: ["Anthropic", "Claude", "AWS", "NVIDIA", "Gemini", "Cloudflare"],
  };
  const BODY: WSection[] = [
    {
      n: "03", name: "The consulting team", layout: "statement", eyebrow: "The consulting team",
      goal: "PILLAR 1 (hero: 'we learn your business') = the CONSULTING TEAM / partner.",
      headline: "Consulting that understands your business",
      sub: "A consulting team comes in and learns how your business actually works — your processes, your systems, your people — then stays as your partner, from the first plan through the long-term strategy.",
      chips: ["Understands you", "Builds the plan", "Partners for the journey"],
    },
    {
      n: "04", name: "The platform", layout: "statement", eyebrow: "The platform",
      goal: "PILLAR 2 (hero: 'connect your systems') = the PLATFORM. Where Wonderful says 'platform', we say 'connect' — so SAY platform; we build one.",
      headline: "Platform that connects your systems",
      sub: "A platform that connects every system, tool, and data source into one place — securely and fully governed — so your whole business finally runs from a single, trusted foundation.",
      chips: ["One platform", "Secure", "Governed"],
    },
    {
      n: "05", name: "The intelligence layer", layout: "statement", eyebrow: "The intelligence layer",
      goal: "PILLAR 3 (hero: 'develop the mind') = the intelligence layer (the AI data layer) — the mind of your business, the client's IP. Naming steered 2026-07-09 (flagged: BI connotation, pending confirm).",
      headline: "The intelligence layer that drives your business growth",
      sub: "On that platform we design your business's intelligence layer — the mind of your business. It knows how you work and does the work, and it's an asset you own: your data, your IP, compounding as you grow.",
      chips: ["Your intelligence layer", "Your company's IP", "Yours to keep"],
    },
  ];
  const PROOF_S: WSection = {
    n: "06", name: "Proof", layout: "proof",
    goal: "Real outcomes, after the story — MAX + Flame King. Qualitative until numbers are confirmed.",
    headline: "Already running for real businesses",
    cards: [
      { t: "MAX Security", b: "AI built and run for their operations — the routine work now handled end to end." },
      { t: "Flame King", b: "A freight operation, now running on AI it never had to build." },
    ],
  };
  const CLOSE_S: WSection = {
    n: "07", name: "The close", layout: "cta",
    goal: "One calm, confident action.",
    headline: "Transform how your business runs",
    sub: "Start with a conversation. We'll show you where to begin, and what your first results could look like.",
    cta: "Book a meeting",
  };

  out.push({
    id: "wf-v1", type: "wireframe", position: { x: WF_X, y: 140 },
    data: { w: 820, label: "Homepage · locked copy", sections: [LOCK_HERO, LOCK_LOGOS, ...BODY, PROOF_S, CLOSE_S] },
    draggable: false, selectable: false,
  });

  return out;
}
